import { friendlyGeneName } from '@resistapp/client/components/selected-map-sampling-point/site-details/site-details-utils';
import { theme } from '@resistapp/client/components/shared/theme';
import { useAssayContext } from '@resistapp/client/contexts/assay-context';
import { useSampleDataContext } from '@resistapp/client/contexts/sample-data-context';
import { useChartDisplayMode } from '@resistapp/client/contexts/use-overview-context/chart-display-mode-context';
import { useTrendChartContext } from '@resistapp/client/contexts/use-overview-context/trendchart-context';
import { useOverviewContext } from '@resistapp/client/contexts/use-overview-context/use-overview-context';
import {
  GLOBAL,
  ReferenceContinent,
  ReferenceSamplingPointType,
  ReferenceTarget,
  type ReferenceByMAEC,
} from '@resistapp/common/api-types';
import { L2Targets } from '@resistapp/common/assays';
import { friendlyL2Target, sentanceCase } from '@resistapp/common/friendly';
import {
  friendlySampleSubType,
  friendlySampleType,
  SampleSubType,
  SampleType,
} from '@resistapp/common/sampling-point-types';
import { AbundanceMode, ChartDisplayMode, MetricMode, ProcessMode, ReferenceMetric } from '@resistapp/common/types';
import { useOverviewReferenceLevels } from './hooks/use-overview-reference-level';

export interface ReferenceLineProps {
  value: number;
  text: string;
  tooltipText: string;
  lineProps: {
    strokeWidth: number;
    strokeDasharray: string;
    strokeLinecap: 'round';
    stroke: string;
  };
}

export function useGeographicalReferenceLines(): ReferenceLineProps[] {
  const trendChartContext = useTrendChartContext();
  const { chartDisplayMode } = useChartDisplayMode();
  const { getAssayInfo } = useAssayContext();
  const {
    queryFilters: {
      filters: { selectedTargets },
    },
  } = useSampleDataContext();
  const {
    metricMode,
    selectedSiteDatum,
    effectiveSiteProcessMode: effectiveSiteDetailsProcessMode,
  } = useOverviewContext();

  const referenceMetric = chartDisplayMode === ChartDisplayMode.METRIC ? metricMode : AbundanceMode.COPIES_PER_L;
  const continentMean = useOverviewReferenceLevels(referenceMetric)[0] as number | undefined;
  const continent: ReferenceContinent | undefined = trendChartContext.continent;

  const referenceSamplingPointType =
    metricMode === MetricMode.REDUCTION
      ? selectedSiteDatum?.samplingPoint.type
      : effectiveSiteDetailsProcessMode === ProcessMode.BEFORE
        ? selectedSiteDatum?.samplingPoint.subtype
        : selectedSiteDatum?.samplingPointAfter?.subtype;

  const selectedAssay = selectedTargets.length === 1 && selectedTargets[0].startsWith('AY') && selectedTargets[0];

  const doColor = metricMode === MetricMode.ARGI && chartDisplayMode === ChartDisplayMode.METRIC;
  const antibioticName = selectedAssay
    ? ''
    : selectedTargets.length === 1
      ? friendlyL2Target(selectedTargets[0] as L2Targets)
      : '';

  const isGlobal = (continent as string) === GLOBAL;
  const continentPrefix = isGlobal ? 'Global ' : `${sentanceCase(continent || '')}an`;

  const typeString =
    metricMode === MetricMode.REDUCTION
      ? friendlySampleType(referenceSamplingPointType as SampleType)
      : selectedSiteDatum?.samplingPoint.type &&
        friendlySampleSubType(referenceSamplingPointType as SampleSubType, selectedSiteDatum.samplingPoint.type);

  if (metricMode === MetricMode.REDUCTION && chartDisplayMode === ChartDisplayMode.ABUNDANCES) {
    // Not supported for now (we'd need a double line per continent)
    return [];
  }

  const meanShort = chartDisplayMode === ChartDisplayMode.ABUNDANCES ? `geom. mean` : 'mean';
  const meanLong = chartDisplayMode === ChartDisplayMode.ABUNDANCES ? `geometric mean` : 'mean';
  const name = selectedAssay
    ? friendlyGeneName(selectedAssay, getAssayInfo(selectedAssay)?.gene || '')
    : antibioticName;
  const metric =
    chartDisplayMode === ChartDisplayMode.ABUNDANCES
      ? 'gene copies per litre'
      : metricMode === MetricMode.ARGI
        ? 'Index'
        : metricMode === MetricMode.REDUCTION
          ? 'Reduction'
          : 'Risk score';
  const continentRefLine =
    continent && continentMean
      ? {
          value: continentMean,
          lineProps: {
            stroke: doColor
              ? continentMean >= 2
                ? theme.colors.levelMod
                : theme.colors.levelLow
              : theme.colors.neutral400,
            strokeDasharray: '8,8',
            strokeWidth: 2.5,
            strokeLinecap: 'round' as const,
          },
          text: `${continentPrefix} ${meanShort} ${name ? `for ${name} ` : metric.toLowerCase()}`,
          tooltipText: `${continentPrefix} ${meanLong} ${metric} for ${name} in ${typeString} samples in the Resistomap database.`,
        }
      : undefined;

  const referenceLines = [];
  if (continentRefLine) {
    referenceLines.push(continentRefLine);
  }

  return referenceLines.sort((a, b) => a.value - b.value);
}

export function getReference(
  metricMode: ReferenceMetric,
  assayOrTarget: ReferenceTarget,
  etype: ReferenceSamplingPointType,
  continent: ReferenceContinent,
  referenceData: ReferenceByMAEC,
): number | null | undefined {
  return referenceData[metricMode]?.[assayOrTarget]?.[etype]?.[continent]?.mean;
}
